// Billing state — the active restaurant's plan as the server reports it
// (billing-signup.md §5.3). Shape follows useAdminStatus: one refresh, one
// reset, and a `loaded` flag so gates can tell "still asking" from "basic".
//
// The client never computes entitlement. `plan` is whatever
// `GET /api/billing/:restaurant_pubkey` answered; the server decides from a
// live subscription, the past-due grace, or a comp grant.

import { create } from 'zustand'
import { fetchPlanSnapshot } from '@/services/billing'
import type { CompSource, Plan, PlanSnapshot, PlanSource, BillingInterval } from '@/services/billing'
import type { LoyaltyServiceContext } from '@/services/loyalty'

interface BillingState {
  /** The restaurant these fields describe; a different active team reloads. */
  restaurantPubkey: string | null
  plan: Plan
  source: PlanSource
  status: string | null
  interval: BillingInterval | null
  currentPeriodEnd: string | null
  cancelAtPeriodEnd: boolean
  cancelAt: string | null
  compSource: CompSource | null
  compExpiresAt: string | null
  hasStripeCustomer: boolean
  loaded: boolean
  loading: boolean
  error: string | null

  refresh: (ctx: LoyaltyServiceContext) => Promise<void>
  apply: (restaurantPubkey: string, snapshot: PlanSnapshot) => void
  reset: () => void
}

const initial = {
  restaurantPubkey: null as string | null,
  plan: 'basic' as Plan,
  source: 'none' as PlanSource,
  status: null as string | null,
  interval: null as BillingInterval | null,
  currentPeriodEnd: null as string | null,
  cancelAtPeriodEnd: false,
  cancelAt: null as string | null,
  compSource: null as CompSource | null,
  compExpiresAt: null as string | null,
  hasStripeCustomer: false,
  loaded: false,
  loading: false,
  error: null as string | null,
}

const LS_PLAN_PREFIX = 'synvya_plan_'

/** The last plan the server answered for a restaurant, or null. `synvya_`-prefixed so logout's sweep clears it. */
export function readLastKnownPlan(restaurantPubkey: string): Plan | null {
  try {
    const v = localStorage.getItem(LS_PLAN_PREFIX + restaurantPubkey)
    return v === 'basic' || v === 'premium' ? v : null
  } catch {
    return null
  }
}

function rememberPlan(restaurantPubkey: string, plan: Plan): void {
  try {
    localStorage.setItem(LS_PLAN_PREFIX + restaurantPubkey, plan)
  } catch {
    // localStorage unavailable — non-fatal
  }
}

function fromSnapshot(restaurantPubkey: string, s: PlanSnapshot) {
  rememberPlan(restaurantPubkey, s.plan)
  return {
    restaurantPubkey,
    plan: s.plan,
    source: s.source,
    status: s.status,
    interval: s.interval,
    currentPeriodEnd: s.current_period_end,
    cancelAtPeriodEnd: s.cancel_at_period_end,
    cancelAt: s.cancel_at ?? null,
    compSource: s.comp_source,
    compExpiresAt: s.comp_expires_at,
    hasStripeCustomer: s.has_stripe_customer,
    loaded: true,
    loading: false,
    error: null,
  }
}

export const useBilling = create<BillingState>((set, get) => ({
  ...initial,

  refresh: async (ctx) => {
    if (get().loading) return
    // A new restaurant: drop the previous one's answer before asking.
    if (get().restaurantPubkey !== ctx.restaurantPubkey) {
      set({ ...initial, restaurantPubkey: ctx.restaurantPubkey, loading: true })
    } else {
      set({ loading: true, error: null })
    }
    try {
      const snapshot = await fetchPlanSnapshot(ctx)
      set(fromSnapshot(ctx.restaurantPubkey, snapshot))
    } catch (err) {
      // Unknown is not Basic. `loaded` stays false so gates fail open and
      // the server, which enforces where it matters, has the last word.
      set({
        loading: false,
        error: err instanceof Error ? err.message : 'Could not load the plan.',
      })
    }
  },

  apply: (restaurantPubkey, snapshot) => set(fromSnapshot(restaurantPubkey, snapshot)),

  reset: () => set({ ...initial }),
}))

/** True only when the server has answered Premium for the active restaurant. */
export function useIsPremium(): boolean {
  return useBilling((s) => s.loaded && s.plan === 'premium')
}
